import { useEffect } from "react";
import { toneOf } from "@/lib/imageTone";

/**
 * Gives every transparent product photo in a storefront the tonal gradient backdrop, in EVERY theme (current and future),
 * without any theme having to ask for it. StoreShell calls this once; it watches the page and, for each <img> whose source
 * is one of the store's product photos, checks (once per URL, only when it is near the screen) whether the photo is
 * transparent. If so the image's frame gets `data-tone-box` + a `--tone-bg` gradient and the image `data-tone`
 * (CSS in index.css shows it whole, with padding and a soft shadow). Opaque photos are left untouched.
 *
 * Opt out for a specific image with `data-tone-skip` (themes that draw their own backdrop, like the Aroma hero).
 */
const clear = (img) => {
  img.removeAttribute("data-tone");
  const box = img._toneBox;
  if (box) { box.removeAttribute("data-tone-box"); box.style.removeProperty("--tone-bg"); img._toneBox = null; }
};

export function useProductTones(rootRef, products, enabled = true) {
  useEffect(() => {
    const root = rootRef.current;
    if (!enabled || !root || typeof MutationObserver === "undefined") return undefined;
    const urls = new Set();
    (products || []).forEach((p) => {
      [p.image, ...(p.images || []), ...((p.variant_items || []).map((v) => v.image))].forEach((u) => u && urls.add(u));
    });
    if (!urls.size) return undefined;

    const seen = new WeakMap();      // img -> the src this run has already handled (a re-run starts fresh)
    const io = typeof IntersectionObserver !== "undefined" ? new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { io.unobserve(e.target); run(e.target); } });
    }, { rootMargin: "300px" }) : null;

    function run(img) {
      const src = img.getAttribute("src");
      toneOf(src).then((tone) => {
        if (!tone || !img.isConnected || img.getAttribute("src") !== src) return;
        let box = img.parentElement;
        if (box && getComputedStyle(box).display === "inline") box = box.parentElement;
        if (!box) return;
        box.setAttribute("data-tone-box", "");
        box.style.setProperty("--tone-bg", tone.bg);
        img.setAttribute("data-tone", "");
        img._toneBox = box;
      });
    }

    function consider(img) {
      if (img.hasAttribute("data-tone-skip") || img.closest("[data-transparent],[data-tone-skip]")) return;
      const src = img.getAttribute("src");
      if (!src || !urls.has(src) || /\.jpe?g(\?|#|$)/i.test(src)) return;   // JPEGs have no transparency: skip the pixel check
      if (seen.get(img) === src) return;
      clear(img);
      seen.set(img, src);
      if (io) io.observe(img); else run(img);
    }

    const scan = (node) => {
      if (node.nodeType !== 1) return;
      if (node.tagName === "IMG") consider(node);
      node.querySelectorAll?.("img").forEach(consider);
    };

    scan(root);
    const mo = new MutationObserver((muts) => {
      muts.forEach((m) => {
        if (m.type === "attributes") consider(m.target);
        else m.addedNodes.forEach(scan);
      });
    });
    mo.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ["src"] });
    return () => { mo.disconnect(); io?.disconnect(); };
  }, [rootRef, products, enabled]);
}
